import Avatar from '@atlaskit/avatar';
import LogoutIcon from '@atlaskit/icon/core/log-out';
import SettingsIcon from '@atlaskit/icon/core/settings';
import ThemeIcon from '@atlaskit/icon/core/theme';
import ArrowRight from '@atlaskit/icon/glyph/arrow-right';
import { ButtonItem, LinkItem, MenuGroup, Section } from '@atlaskit/menu';
import { Profile } from '@atlaskit/navigation-system/top-nav-items';
import Popup from '@atlaskit/popup';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import React, { useState } from 'react';

import { ThemeSection } from './theme';
import { getManageProfileLink } from './utils';

type UserInfoProps = {
	name: string | undefined;
	email: string | undefined;
	picture: string | undefined;
	accountId: string;
};

const UserInfo = ({ user }: { user: UserInfoProps }) => {
	return (
		<Box xcss={userInfoStyles}>
			<Avatar src={user.picture} size="large" />
			<Stack>
				<Text maxLines={1} weight="bold">
					{user.name}
				</Text>
				<Text maxLines={1} color="color.text.subtlest">
					{user.email}
				</Text>
			</Stack>
		</Box>
	);
};

const ProfilePopup = ({ user }: { user: UserInfoProps }) => {
	const [isOpen, setIsOpen] = useState(false);
	return (
		<Box onClick={(e: React.MouseEvent) => e.stopPropagation()} xcss={nestedPopupStyles}>
			<MenuGroup>
				<Section titleId="user-profile-section">
					<UserInfo user={user} />
				</Section>
				<Section titleId="user-profile-settings">
					<LinkItem
						href={getManageProfileLink(user?.email || '')}
						target="_blank"
						iconBefore={<SettingsIcon label="" />}
					>
						Account Settings
					</LinkItem>
					<Popup
						isOpen={isOpen}
						placement="left-start"
						onClose={() => setIsOpen(false)}
						content={() => <ThemeSection />}
						trigger={(triggerProps) => (
							<ButtonItem
								{...triggerProps}
								isSelected={isOpen}
								onClick={() => setIsOpen(true)}
								iconBefore={<ThemeIcon label="" />}
								iconAfter={<ArrowRight label="" />}
							>
								Theme
							</ButtonItem>
						)}
					/>
				</Section>
				<Section titleId="user-profile-actions" hasSeparator>
					<LinkItem href={`${window.location.origin}/logout`} iconBefore={<LogoutIcon label="" />}>
						Log Out
					</LinkItem>
				</Section>
			</MenuGroup>
		</Box>
	);
};

export const UserProfile = ({ user }: { user: UserInfoProps }) => {
	const [isOpen, setIsOpen] = useState(false);
	const close = () => setIsOpen(false);

	return (
		<Popup
			isOpen={isOpen}
			onClose={close}
			placement="bottom-end"
			trigger={(triggerProps) => (
				<Profile
					{...triggerProps}
					label="Profile"
					src={user.picture}
					isSelected={isOpen}
					onClick={() => setIsOpen((prevOpen) => !prevOpen)}
				/>
			)}
			content={() => <ProfilePopup user={user} />}
		/>
	);
};

const nestedPopupStyles = xcss({
	boxSizing: 'border-box',
	minWidth: '300px',
	maxWidth: '300px',
});

const userInfoStyles = xcss({
	display: 'flex',
	flexDirection: 'row',
	backgroundColor: 'elevation.surface.sunken',
	columnGap: 'space.100',
	padding: 'space.200',
	marginLeft: 'space.100',
	marginRight: 'space.100',
});
